import React, { useEffect, useState, useRef } from 'react';

import {
    Box,
    Breadcrumbs,
    Divider,
    Link,
    Stack,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListSubheader,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    ListItem,
    Chip,
    Skeleton,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Grid,
    styled
} from '@mui/material';

// import {
//     Search as SearchIcon,
//     Close as CloseIcon,
//     FindInPageOutlined as FindInPageOutlinedIcon,
// } from '@mui/icons-material'

import { CommonIcons, icons } from '../common/CommonIcons';

import { useSystemPrompt } from '../../hooks/useSystemPrompt';
import { useDocuments, documentTypes, targetTypes, configuration as documentConfiguration } from '../../hooks/api/useDocuments';
import { useTranslations } from "../../hooks/useTranslations";

import { useAppContextFeatures } from '../../context/AppContext';

import { capitalizeString } from '../../utils/formatString';

// import { checkValueExists } from '../../utils/dataSchema'

const StyledIcon = styled(Box)(({ theme }) => ({
    width: 20,
    height: 20,
    // opacity: 0.1,
    //   backgroundColor: "green",
    //   color: 'inherit',
    //   display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledTitle = styled(Box)(({ theme }) => ({
    fontWeight: 800,
    fontSize: '1.0rem'
}))

const StyledSubTitle = styled(Box)(({ theme }) => ({
    fontWeight: 300,
    fontSize: '0.8rem'
}))

const filterDocumentTypes = [
    {
        id: 'salesOrder',
        name: "Sales Order",
        value: documentTypes.salesOrder
    },
    {
        id: 'purchaseOrder',
        name: "Purchase Order",
        value: documentTypes.purchaseOrder
    },
    {
        id: 'cashSales',
        name: "Cash Sales",
        value: documentTypes.cashSales
    },
    {
        id: 'retailCashSales',
        name: "Retail Cash Sales",
        value: documentTypes.retailCashSales
    },
    {
        id: 'deliveryOrder',
        name: "Delivery Order",
        value: documentTypes.deliveryOrder
    },
    {
        id: 'replacementDelivery',
        name: "Replacement Delivery",
        value: documentTypes.replacementDelivery
    },
    {
        id: 'retailPrescription',
        name: "Retail Prescription",
        value: documentTypes.retailPrescription
    },
    // {
    //     id: 'itemAdjustment',
    //     name: "Item Adjustments",
    //     value: documentTypes.itemAdjustment
    // }
]

const getAvailableFilterDocumentTypes = ({ config }) => {

    let types = []

    filterDocumentTypes.forEach((e) => {
        if (config?.allowDocumentTypes.includes(e.value)) {
            types.push(e);
        }
    })

    return {
        defaultDocumentType: config?.presetDocumentType ? config.presetDocumentType : "",
        filterDocumentTypes: types
    }
}

export default function SearchDocuments(
    {
        id,
        show = false,
        config,
        customDisplay,
        handleOnCancel,
        handleOnSelect,
        handleOnSearchChange,
        textProps
    }
) {
    const { t } = useTranslations({ ns: ["common", "prompt", "@document"] })

    const searchInputRef = useRef()
    const typeRef = useRef()

    const [open, setOpen] = useState(true)
    const [keyword, setKeyword] = useState(config?.filterKeyword ? config.filterKeyword : "")
    const [data, setData] = useState([])
    const [document, setDocument] = useState()

    const [documentType, setDocumentType] = useState(getAvailableFilterDocumentTypes({ config }).defaultDocumentType)
    const [documentTypeSelection, setDocumentTypeSelection] = useState(getAvailableFilterDocumentTypes({ config }).filterDocumentTypes)

    const { promptMessage } = useSystemPrompt()
    const { isLoading, list, get, filterOperatorTypes } = useDocuments();

    const { setSystemLoading } = useAppContextFeatures()

    const fetchDocuments = async () => {

        try {

            // default filters
            let filters = [
                {
                    name: "doc_number",
                    operator: filterOperatorTypes.contains,
                    value: keyword
                }
            ]

            // for config specify filter for documents - retail sales order
            if (config?.filterValues) {
                filters = filters.concat(config?.filterValues)
            }

            // override for only delivery orders
            if (documentType === documentTypes.deliveryOrder) {
                filters = [
                    {
                        name: "doc_number",
                        operator: filterOperatorTypes.contains,
                        value: keyword,
                        group: "name_group"
                    },
                    {
                        name: "delivery_profile_name",
                        operator: filterOperatorTypes.eqOr,
                        value: keyword,
                        group: "name_group"
                    }
                ]
            }

            // override for only cash sales
            if (
                documentType === documentTypes.cashSales
            ) {
                filters = [
                    {
                        name: "doc_number",
                        operator: filterOperatorTypes.contains,
                        value: keyword,
                        group: "name_group"
                    },
                    {
                        name: "delivery_profile_name",
                        operator: filterOperatorTypes.eqOr,
                        value: keyword,
                        group: "name_group"
                    }
                ]
            }

            // override for only replacement delivery orders
            if (
                documentType === documentTypes.replacementDelivery
            ) {
                filters = [
                    {
                        name: "doc_number",
                        operator: filterOperatorTypes.contains,
                        value: keyword,
                        group: "name_group"
                    },
                    {
                        name: "replacement_delivery_profile_name",
                        operator: filterOperatorTypes.eqOr,
                        value: keyword,
                        group: "name_group"
                    }
                ]
            }

            let props = {
                limit: 30,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: -1,
                },
                filterValue: filters
            }

            if (documentType) props.documentType = documentType

            props.configuration = {
                [documentConfiguration.enableServerClientCancellation.id]: true
            }

            let response = await list(props);
            // console.log('response?.data?.items', response?.data?.items)
            setData(response?.data?.documents ? response?.data?.documents : [])

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }
    }

    const fetchDocument = async ({ id, documentType }) => {

        setSystemLoading(true);

        try {

            const props = {
                id,
                documentType,
                dataTypesArray: []
            }

            console.log('props', props)

            let response = await get(props);

            setDocument(response?.data ? response?.data : [])

            setSystemLoading(false)

            return response?.data

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

        return true;
    }

    useEffect(() => {

        setData([])

    }, [])

    useEffect(() => {

        if (keyword) {
            fetchDocuments({ keyword });
        } else {
            setKeyword("")
            setData([])
        }

    }, [keyword, documentType])

    useEffect(() => {

        const timeout = setTimeout(() => {

            if (open) {
                // searchInputRef?.current.focus();

                // console.log('serachRef', searchInputRef?.current)

                if (documentTypeSelection && documentTypeSelection.length === 1) {
                    searchInputRef?.current.focus();
                } else {
                    typeRef?.current.focus();
                }
            }

        }, 100);

        return () => {
            clearTimeout(timeout);
        };

    }, [open])

    const RenderListData = () => {

        if (isLoading) {
            return (
                [1, 2, 3, 4].map((item, index) => <RenderListDataItem key={index} isLoading={Boolean(true)} />)
            )
        }

        return (
            data.map((item, index) =>
                <RenderListDataItem key={index} item={item} index={index} />
            )
        )
    }

    const RenderListDataItemSkeleton = () => {
        return (
            <>
                <Skeleton
                    animation="wave"
                    // variant="circular" 
                    sx={{
                        minWidth: 100,
                        mr: 1,
                        height: 50
                    }}
                />
                <ListItemText
                // fullWidth
                // height={50}
                >
                    <Skeleton
                        variant="rectangular"
                        width={"100%"}
                        height={20}
                    />
                    <Skeleton
                        variant="rectangular"
                        width={"100%"}
                        height={20}
                        sx={{
                            mt: 1
                        }}
                    />
                </ListItemText>
            </>
        )
    }

    const RenderListDataItemDisplay = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        if (item?.type.toLowerCase() === documentTypes.deliveryOrder.toLowerCase()) {


            let {
                delivery_address_1: address1 = "",
                delivery_address_2: address2 = "",
                delivery_profile_name: profileName = "",
                delivery_postcode: postcode = "",
                delivery_area_name: areaName = "",
                delivery_state_name: stateName = "",
                delivery_country_name: countryName = "",
                delivery_pic_name: picName = "",
                delivery_pic_contact: picContact = "",
                delivery_pic_email: picEmail = ""
            } = item

            address1 = address1 ? address1.toUpperCase() : "-"
            address2 = address2 ? address2.toUpperCase() : "-"
            profileName = profileName ? profileName.toUpperCase() : "-"
            areaName = areaName ? areaName.toUpperCase() : "-"
            stateName = stateName ? stateName.toUpperCase() : "-"
            countryName = countryName ? countryName.toUpperCase() : "-"

            return (
                <>
                    <Grid container padding={1}>
                        <Grid item md={1} textAlign={"center"}>
                            <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                        </Grid>
                        <Grid item md={7}>
                            <StyledTitle sx={{ textTransform: 'uppercase' }}>
                                {`${profileName}`}
                            </StyledTitle>
                            <StyledSubTitle>
                                {`${address1}`} {`${address2}`}
                            </StyledSubTitle>
                            <StyledSubTitle>
                                {`${areaName}`} ({`${postcode}`}) {`${stateName}`} {`${countryName}`}
                            </StyledSubTitle>
                            <StyledSubTitle>
                                {`${picName}`}, {`${picContact}`} ({`${picEmail}`})
                            </StyledSubTitle>
                        </Grid>
                        <Grid item md={4} textAlign={"right"}>
                            <StyledTitle>
                                {`${item?.doc_number}`}
                            </StyledTitle>
                            <StyledSubTitle>
                                {`${item?.doc_date}`}
                            </StyledSubTitle>
                            {
                                userType !== "" &&
                                <Chip
                                    label={`${capitalizeString({ val: userType })}`}
                                    variant={'outlined'}
                                    size={"small"}
                                    color={"primary"}
                                />
                            }
                        </Grid>
                    </Grid>
                </>
            )
        }

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${item?.profile_name}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.address_1}`} {`${item?.address_2}`} {`${item?.postcode}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${item?.contact}`} {`${item?.email}`}
                        </StyledSubTitle>
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${item?.doc_number}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.doc_date}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderListDataItem = ({ isLoading = false, index, item }) => {
        return (
            <ListItem
                key={index}
                divider
                disablePadding
            >
                <ListItemButton
                    role={undefined}
                    onClick={() => {
                        handleSelect({ item, id: item?.id });
                    }}
                    dense
                >
                    {
                        Boolean(isLoading) ? <RenderListDataItemSkeleton /> : <RenderListDataItemDisplay item={item} />

                    }

                </ListItemButton>
            </ListItem>
        )
    }

    const RenderList = () => {

        return (
            <List
                // sx={{ width: '100%', maxWidth: 360,
                //     bgcolor: 'background.paper' }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                subheader={
                    <ListSubheader
                        component="div"
                        id="nested-list-subheader"
                        sx={{
                            // backgroundColor: 'red'
                            // pl: 1,
                            // pr: 1
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                p: 1,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >

                            <Box
                                sx={{
                                    flex: 1,
                                    mr: 1
                                }}
                            >

                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                // error={touched.countryId && Boolean(errors.countryId)}
                                >
                                    <InputLabel id="documentType">{t('document')}</InputLabel>
                                    <Select
                                        inputRef={typeRef}
                                        labelId="documentType"
                                        label={t('document')}
                                        name={"documentType"}
                                        onChange={(event) => {
                                            setDocumentType(event.target.value)
                                            // setFieldValue(`countryId`, event.target.value)
                                            // setFieldValue(`stateId`, "")
                                            // setFieldValue(`areaId`, "")
                                            // setFieldValue(`postcode`, "")
                                            // setDataStates([])
                                            // setDataAreas([])
                                        }}
                                        value={documentType}
                                        sx={{
                                            textAlign: 'left'
                                        }}
                                    >
                                        {
                                            documentTypeSelection.map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={i.value}>{i.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    {/* <FormHelperText>
                                            {touched.countryId && errors.countryId}
                                        </FormHelperText> */}
                                </FormControl>
                            </Box>
                            <Box
                                sx={{
                                    flex: 2,
                                }}
                            >
                                <TextField
                                    inputRef={searchInputRef}
                                    autoComplete="off"
                                    margin="dense"
                                    size="small"
                                    label={t("searchKeyword")}
                                    fullWidth
                                    variant="outlined"
                                    onChange={e => {
                                        setKeyword(e.target.value)
                                    }}
                                    value={keyword}
                                    InputProps={{
                                        startAdornment:
                                            <InputAdornment position="start">
                                                <CommonIcons name={icons.search} />
                                            </InputAdornment>
                                    }}
                                />
                            </Box>
                        </Paper>
                    </ListSubheader>
                }
            >
                {
                    RenderListData()
                }

            </List>
        )
    }

    const handleExit = () => {
        setOpen(false);

        if (handleOnCancel)
            handleOnCancel();
    }

    const handleSelect = async ({ id, item }) => {

        let document = await fetchDocument({
            id,
            documentType
        })

        if (handleOnSelect)
            handleOnSelect({ id, data: document })

        promptMessage(true, t(`selected1Item`))
        setOpen(false);
    }

    const RenderSearch = () => {

        // check the custom display is set
        if (customDisplay) {
            return customDisplay({ setOpen })
        }

        return (
            <TextField
                disabled={textProps?.disabled ? textProps.disabled : false}
                margin="normal"
                size="small"
                fullWidth
                name={id}
                label="Name"
                type="text"
                variant="outlined"
                value={textProps?.value ? textProps.value : ""}
                onChange={(val) => handleOnSearchChange(val)}
                error={textProps?.error ? textProps.error : false}
                helperText={textProps?.helperText}
                InputProps={{
                    style: {
                        paddingRight: 5
                    },
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="open"
                                onClick={() => setOpen(true)}
                            >
                                <CommonIcons name={icons.search} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        )
    }

    return (
        <Box>

            {/* { RenderSearch() } */}

            <Dialog
                sx={{
                    '& .MuiDialog-paper':
                    {
                        // width: '80%', 
                        minHeight: '90vh'
                    }
                }}
                fullWidth={Boolean(true)}
                maxWidth="md"
                // TransitionProps={{ onEntering: handleEntering }}
                open={open}
                // {...other}
                onClose={handleExit}
            >
                <DialogTitle
                >
                    {t('searchDocuments')}

                    <IconButton
                        aria-label="close"
                        onClick={handleExit}
                        sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CommonIcons name={icons.cancel} />
                    </IconButton>

                </DialogTitle>
                <DialogContent
                    dividers
                    // minHeight="100%"
                    sx={{
                        // backgroundColor: 'red',
                        pl: 0,
                        pr: 0
                    }}
                >
                    {
                        RenderList()
                    }

                </DialogContent>
                {/* <DialogActions>
                    <Button 
                        autoFocus 
                        // onClick={handleCancel}
                        onClick={() => {
                            handleOnCancel()
                        }}
                    >Cancel</Button>
                    <Button 
                        // onClick={handleOk}
                        onClick={() => {
                            handleOnConfirm()
                        }}
                    >Ok</Button>
                </DialogActions> */}
            </Dialog>

        </Box>
    )
}